<script setup lang="ts">
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
    Button,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ToggleGroup,
    ToggleGroupItem,
    Avatar,
    AvatarFallback,
    AvatarImage
  } from '@gat-ship/vue-components'
  import {
    IconChevronDown,
    IconThemeAuto,
    IconThemeDark,
    IconThemeLight
  } from '@gat-ship/icons'
  import { useFormatting } from '@gat-ship/utils'
  import { useUser } from '@gat-ship/user'
  import { useGeneralPreferences } from '../composables/useGeneralPreferences'

  const user = useUser()
  const { userPreferences } = user
  const { formatPhoneNumber } = useFormatting()

  const { popupStyleLabel, setModalType, setTheme } = useGeneralPreferences()
</script>

<template>
  <div class="flex flex-col gap-6">
    <ModalSection v-if="user.userProfile.value">
      <ModalSectionHeader class="pt-1">
        <ModalSectionHeaderTitle>Profile</ModalSectionHeaderTitle>
      </ModalSectionHeader>
      <div class="gap-0 divide-y">
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium">Avatar</span>
          <Avatar class="size-10">
            <AvatarImage
              v-if="user.userProfile.value?.profileImageUrl"
              :src="user.userProfile.value?.profileImageUrl"
            />
            <AvatarFallback />
          </Avatar>
        </div>
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium">Full Name</span>
          <span class="text-muted-foreground text-sm">
            {{ user.userProfile.value.fullName }}
          </span>
        </div>
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium">Email</span>
          <span class="text-muted-foreground text-sm">
            {{ user.userProfile.value.email.toLowerCase() }}
          </span>
        </div>
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium">Phone</span>
          <span class="text-muted-foreground text-sm">
            {{ formatPhoneNumber(user.userProfile.value.mobilePhoneNumber) }}
          </span>
        </div>
      </div>
    </ModalSection>

    <!-- Preferences section -->
    <ModalSection>
      <ModalSectionHeader>
        <ModalSectionHeaderTitle>Preferences</ModalSectionHeaderTitle>
      </ModalSectionHeader>
      <div class="flex flex-col divide-y">
        <div class="flex items-center justify-between gap-4 py-3">
          <div class="flex flex-col">
            <span class="text-sm font-medium">Popup Style</span>
            <span class="text-muted-foreground text-xs">
              Choose how the popup appears.
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="sm">
                {{ popupStyleLabel }}
                <IconChevronDown class="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-64">
              <DropdownMenuRadioGroup
                :model-value="userPreferences.getUserPreferences().modalType"
                @update:model-value="setModalType"
              >
                <DropdownMenuRadioItem value="dialog">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium">Dialog</span>
                    <span class="text-muted-foreground text-xs">
                      Centered on the screen.
                    </span>
                  </div>
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="drawer">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium">Drawer</span>
                    <span class="text-muted-foreground text-xs">
                      Slides in from the side.
                    </span>
                  </div>
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div class="flex items-center justify-between gap-4 py-3">
          <div class="flex flex-col">
            <span class="text-sm font-medium">Appearance</span>
            <span class="text-muted-foreground text-xs">
              Choose the color scheme for the application.
            </span>
          </div>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            :model-value="userPreferences.getUserPreferences().theme"
            @update:model-value="setTheme"
          >
            <ToggleGroupItem
              value="light"
              :icon="IconThemeLight"
              label="Light"
            />
            <ToggleGroupItem value="dark" :icon="IconThemeDark" label="Dark" />
            <ToggleGroupItem
              value="auto"
              :icon="IconThemeAuto"
              label="System"
            />
          </ToggleGroup>
        </div>
      </div>
    </ModalSection>
  </div>
</template>
